import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Calendar, ArrowRight, SpinnerGap, CheckCircle } from '@phosphor-icons/react';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';
import SEO from '@/components/SEO';

const POSTS_PER_PAGE = 12;
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=800';

// Resolve image src - /api/ paths stay relative (same-origin via Nginx)
const resolveImageSrc = (src) => {
  if (!src) return FALLBACK_IMAGE;
  return src;
};

// Blog Thumbnail component
const BlogThumbnail = ({ src, alt }) => {
  const imgSrc = resolveImageSrc(src);

  return (
    <img
      src={imgSrc}
      alt={alt}
      className="w-full h-full object-cover"
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={(e) => {
        if (!e.target.src.includes('unsplash.com')) {
          e.target.src = FALLBACK_IMAGE;
        }
      }}
    />
  );
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPosts, setTotalPosts] = useState(0);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Fetch posts with pagination
  const fetchPosts = useCallback(async (pageNum, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const skip = pageNum * POSTS_PER_PAGE;
      const response = await api.get(`/blog?limit=${POSTS_PER_PAGE}&skip=${skip}&published_only=true`);
      const newPosts = response.data || [];
      
      if (append) {
        setPosts(prev => [...prev, ...newPosts]);
      } else {
        setPosts(newPosts);
        
        // Get total count on first load
        const countResponse = await api.get('/blog?limit=500&published_only=true');
        const total = countResponse.data?.length || 0;
        setTotalPosts(total);
      }

      // Check if there are more posts
      setHasMore(newPosts.length === POSTS_PER_PAGE);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchPosts(0);
  }, [fetchPosts]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !loadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchPosts(nextPage, true);
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observerRef.current = observer;

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, loadingMore, page, fetchPosts]);

  const featuredPost = posts.find(p => p.is_featured) || posts[0];
  const otherPosts = posts.filter(p => p !== featuredPost);

  return (
    <>
      <SEO
        title="Wine Country Blog - Napa Valley & Sonoma Guides"
        description="Expert guides to Napa Valley and Sonoma wine country. First-time visitor itineraries, wine tasting tips, deals, and insider recommendations."
        path="/blog"
        image="https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=1200"
        noindex={!loading && posts.length === 0}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Blog', path: '/blog' }
        ]}
      />

      {/* Hero */}
      <section 
        className="pt-24 sm:pt-32 pb-10 sm:pb-16 bg-gradient-to-b from-wine-900 to-wine-800"
        data-testid="blog-hero"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-gold-400 text-xs sm:text-sm font-medium mb-3 sm:mb-4 block">Wine Country Insights</span>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-6xl text-white mb-3 sm:mb-4">
              The <span className="italic text-gold-400">Winery Finder</span> Blog
            </h1>
            <p className="text-white/70 text-sm sm:text-base max-w-2xl mx-auto px-4">
              Expert guides, insider tips, and everything you need to plan your perfect wine country adventure.
            </p>
            {totalPosts > 0 && (
              <div className="mt-4 sm:mt-6 inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3 sm:px-4 py-1.5 sm:py-2">
                <BookOpen size={16} className="text-gold-400 sm:w-[18px] sm:h-[18px]" />
                <span className="text-white/90 text-xs sm:text-sm">{totalPosts} articles to explore</span>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {loading ? (
        <div className="py-16 text-center bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wine-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading articles...</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="py-16 text-center bg-gray-50">
          <BookOpen size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No blog posts yet. Check back soon!</p>
        </div>
      ) : (
        <>
          {/* Featured Post */}
          {featuredPost && (
            <section className="py-8 sm:py-12 bg-white" data-testid="featured-post">
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <Link 
                  to={`/blog/${featuredPost.slug}`}
                  className="group grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 items-center"
                  data-testid={`featured-post-${featuredPost.slug}`}
                >
                  <motion.div 
                    className="aspect-video sm:aspect-[16/10] overflow-hidden rounded-xl sm:rounded-2xl shadow-lg"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <BlogThumbnail 
                      src={featuredPost.featured_image}
                      alt={featuredPost.title}
                    />
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="mt-4 md:mt-0 min-w-0"
                  >
                    <span className="inline-block px-2 sm:px-3 py-1 bg-gold-100 text-gold-700 text-xs sm:text-xs font-medium rounded-full mb-2 sm:mb-4">
                      Featured • {featuredPost.category}
                    </span>
                    <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-gray-900 mb-2 sm:mb-4 group-hover:text-wine-600 transition-colors break-words">
                      {featuredPost.title}
                    </h2>
                    <p className="text-gray-600 text-sm sm:text-base mb-3 sm:mb-4 line-clamp-2 sm:line-clamp-3">
                      {featuredPost.excerpt}
                    </p>
                    <div className="flex items-center gap-3 sm:gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar size={12} className="sm:w-[14px] sm:h-[14px]" />
                        {formatDate(featuredPost.created_at)}
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-wine-600 font-medium mt-3 sm:mt-4 text-sm sm:text-base group-hover:gap-2 transition-all">
                      Read Article <ArrowRight size={14} className="sm:w-4 sm:h-4" />
                    </span>
                  </motion.div>
                </Link>
              </div>
            </section>
          )}


          {/* Infinite Scroll Blog Grid */}
          {otherPosts.length > 0 && (
            <section className="py-8 sm:py-12 bg-gray-50" data-testid="blog-grid">
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6 sm:mb-8">
                  <h2 className="font-serif text-xl sm:text-2xl text-gray-900">More Articles</h2>
                  <span className="text-sm text-gray-500">
                    Showing {posts.length} of {totalPosts} articles
                  </span>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
                  <AnimatePresence>
                    {otherPosts.map((post, index) => (
                      <React.Fragment key={post.id}>
                        <motion.article
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{ delay: Math.min(index * 0.05, 0.3) }}
                          layout
                        >
                          <Link 
                            to={`/blog/${post.slug}`}
                            className="group block bg-white rounded-lg sm:rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
                            data-testid={`blog-card-${post.slug}`}
                          >
                            <div className="aspect-video sm:aspect-[16/9] overflow-hidden bg-gray-100">
                              <BlogThumbnail 
                                src={post.featured_image}
                                alt={post.title}
                              />
                            </div>
                            <div className="p-4 sm:p-5">
                              <span className="inline-block px-2 py-1 bg-wine-50 text-wine-600 text-xs sm:text-xs font-medium rounded-full mb-2 sm:mb-3">
                                {post.category}
                              </span>
                              <h3 className="font-serif text-base sm:text-lg text-gray-900 mb-1.5 sm:mb-2 group-hover:text-wine-600 transition-colors line-clamp-2">
                                {post.title}
                              </h3>
                              <p className="text-gray-500 text-sm line-clamp-2 mb-3 sm:mb-4">
                                {post.excerpt}
                              </p>
                              <div className="flex items-center text-xs sm:text-xs text-gray-400">
                                <span className="flex items-center gap-1">
                                  <Calendar size={10} className="sm:w-3 sm:h-3" />
                                  {formatDate(post.created_at)}
                                </span>
                              </div>
                            </div>
                          </Link>
                        </motion.article>
                      </React.Fragment>
                    ))}
                  </AnimatePresence>
                </div>

                {/* Infinite Scroll Trigger / Loading Indicator */}
                <div 
                  ref={loadMoreRef}
                  className="flex flex-col items-center justify-center py-12"
                >
                  {loadingMore && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex flex-col items-center gap-3"
                    >
                      <SpinnerGap size={32} className="text-wine-600 animate-spin" />
                      <span className="text-gray-500 text-sm">Loading more articles...</span>
                    </motion.div>
                  )}
                  
                  {!hasMore && posts.length > POSTS_PER_PAGE && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex flex-col items-center gap-3 text-center"
                    >
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircle size={24} className="text-green-600" weight="fill" />
                      </div>
                      <div>
                        <p className="text-gray-900 font-medium">You've seen all {totalPosts} articles!</p>
                        <p className="text-gray-500 text-sm mt-1">Check back soon for new wine country insights</p>
                      </div>
                    </motion.div>
                  )}
                  
                  {hasMore && !loadingMore && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center"
                    >
                      <p className="text-gray-400 text-sm">Scroll for more articles</p>
                      <div className="mt-2 flex justify-center gap-1">
                        <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Progress indicator */}
                {posts.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                      <span>Progress</span>
                      <span>{Math.round((posts.length / totalPosts) * 100)}%</span>
                    </div>
                    <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-gradient-to-r from-wine-500 to-gold-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${(posts.length / totalPosts) * 100}%` }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

        </>
      )}
    </>
  );
};

export default Blog;
